import type { Variants } from 'framer-motion';

/* ── Animation constants (matches journal exactly) ───── */

export const EASE = [0.22, 1, 0.36, 1] as const;

export const sectionVariants: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } },
};

export const itemVariants: Variants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

/* ── Theme color system ──────────────────────────────── */

export type SectionTheme = 'teal' | 'ivory';

export function sectionColors(theme: SectionTheme) {
    const isDark = theme === 'teal';
    return {
        bg: isDark ? 'bg-[#0e2727]' : 'bg-[#fdfaf3]',
        heading: isDark ? 'text-[#F5F5F5]' : 'text-[#0e2727]',
        kicker: isDark ? 'text-[#F5F5F5]/60' : 'text-[#0e2727]/60',
        body: isDark ? 'text-[#F5F5F5]/75' : 'text-[#0e2727]/75',
        rule: isDark ? 'border-[#F5F5F5]/15' : 'border-[#0e2727]/15',
        label: isDark ? 'text-[#F5F5F5]/60' : 'text-[#0e2727]/60',
        value: isDark ? 'text-[#F5F5F5]' : 'text-[#0e2727]',
        muted: isDark ? 'text-[#F5F5F5]/50' : 'text-[#0e2727]/50',
    };
}

/* ── Forbes rating icon helper ───────────────────────── */

/**
 * Returns the public SVG icon path for a Forbes Travel Guide rating.
 *
 * Ratings:  5 = Five-Star  |  4 = Four-Star  |  3 = Recommended
 */
export function getForbesIcon(rating: number | undefined): string | null {
    switch (rating) {
        case 5:
            return '/forbes-rating-5-star-icon.svg';
        case 4:
            return '/forbes-rating-4-star-icon.svg';
        case 3:
            return '/forbes-rating-R-star-icon.svg';
        default:
            return null;
    }
}

/* ── Phone formatter ─────────────────────────────────── */

/**
 * Formats a phone string for human-readable display.
 * Handles common international formats; passes through
 * already-formatted or unrecognised strings unchanged.
 */
export function formatPhone(phone: string | undefined): string {
    if (!phone) return '';

    // Strip all non-digit and non-plus characters for normalisation
    const digits = phone.replace(/[^\d+]/g, '');

    // US / Canada: +1XXXXXXXXXX  ->  +1 (XXX) XXX-XXXX
    const usMatch = digits.match(/^\+?1(\d{3})(\d{3})(\d{4})$/);
    if (usMatch) {
        return `+1 (${usMatch[1]}) ${usMatch[2]}-${usMatch[3]}`;
    }

    // UK: +44XXXXXXXXXX  ->  +44 XXXX XXXXXX
    const ukMatch = digits.match(/^\+?44(\d{4})(\d{6})$/);
    if (ukMatch) {
        return `+44 ${ukMatch[1]} ${ukMatch[2]}`;
    }

    // Generic international: insert space after country code (+XX XXXXXXXXXX)
    const intlMatch = digits.match(/^\+(\d{1,3})(\d+)$/);
    if (intlMatch) {
        return `+${intlMatch[1]} ${intlMatch[2]}`;
    }

    // Return original if no pattern matched (already formatted)
    return phone;
}

/* ── Currency formatter ──────────────────────────────── */

/**
 * Formats a numeric amount as a localised currency string.
 * Falls back gracefully when the currency code is unknown.
 */
export function formatCurrency(amount: number, currency = 'USD'): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        // Unknown currency code — fall back to plain number with code suffix
        return `${amount.toFixed(2)} ${currency}`;
    }
}

/* ── GDS provider display names ──────────────────────── */

export const GDS_PROVIDER_NAMES: Record<string, string> = {
    sabre: 'SABRE GDS',
    sabre_tn: 'SABRE GDS',
    sabre_csl: 'SABRE GDS',
    amadeus: 'AMADEUS GDS',
    galileo: 'GALILEO GDS',
    worldspan: 'WORLDSPAN GDS',
    apollo: 'APOLLO GDS',
    pegasus: 'PEGASUS',
    derbysoft: 'DERBYSOFT',
};
